import { Injectable } from '@angular/core';

/**
 * Subset of ngx-toastr's per-call config that call sites actually pass.
 */
export interface ToastrOverride {
  timeOut?: number;
}

type ToastVariant = 'info' | 'success' | 'warning' | 'error';

interface ToastManagerElement extends HTMLElement {
  show(options: { message: string; title?: string; variant?: ToastVariant; duration?: number }): Promise<number>;
  dismissAll(): Promise<void>;
}

/**
 * Drop-in replacement for ngx-toastr's `ToastrService`, backed by the design
 * system's `<sh-toast-manager>`. ngx-toastr has no Angular 22 release, and
 * keeping its method names and argument order means the ~1,300 call sites
 * across the app did not have to change — only the import path.
 *
 * The manager element is created on first use and lives on document.body, so
 * toasts are not clipped by whatever container happens to be showing.
 */
@Injectable({ providedIn: 'root' })
export class ToastrService {
  private manager?: ToastManagerElement;

  success(message?: string, title?: string, override?: ToastrOverride): void {
    this.show('success', message, title, override);
  }

  error(message?: string, title?: string, override?: ToastrOverride): void {
    this.show('error', message, title, override);
  }

  warning(message?: string, title?: string, override?: ToastrOverride): void {
    this.show('warning', message, title, override);
  }

  info(message?: string, title?: string, override?: ToastrOverride): void {
    this.show('info', message, title, override);
  }

  /** Dismisses every visible toast. */
  clear(): void {
    void this.manager?.dismissAll();
  }

  private show(variant: ToastVariant, message?: string, title?: string, override?: ToastrOverride): void {
    const manager = this.resolveManager();
    if (!manager) return;
    void manager.show({
      message: message ?? '',
      title,
      variant,
      // Leaving duration undefined lets the manager apply its own default.
      duration: override?.timeOut,
    });
  }

  private resolveManager(): ToastManagerElement | undefined {
    if (this.manager?.isConnected) return this.manager;
    if (typeof document === 'undefined') return undefined;

    const existing = document.querySelector<ToastManagerElement>('sh-toast-manager');
    if (existing) {
      this.manager = existing;
      return existing;
    }

    const created = document.createElement('sh-toast-manager') as ToastManagerElement;
    created.setAttribute('position', 'top-right');
    document.body.appendChild(created);
    this.manager = created;
    return created;
  }
}
